import React, {
  useEffect,
  useState,
} from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../store/hooks";

import {
  fetchPermissionsByRole,
  savePermissions,
  togglePermission,
  selectPermissionsForRole,
  selectPermissionsLoading,
  selectPermissionsSaving,
  ALL_MODULES,
} from "../../store/slices/permissionSlice";

import RoleApi from "../../api/RoleApi";

import {
  showError,
  showSuccess,
} from "../../utils/toastUtils";


/* =========================================
   DEFAULT PERMISSIONS
========================================= */

const buildDefaultPermissions = () => {

  return ALL_MODULES.reduce(
    (acc, { key }) => {

      acc[key] = {
        canRead: false,
        canWrite: false,
        canDelete: false,
      };

      return acc;

    },
    {}
  );

};


/* =========================================
   SUPERADMIN PERMISSIONS
========================================= */

const SUPERADMIN_PERMS = ALL_MODULES.reduce(
  (acc, { key }) => {

    acc[key] = {
      canRead: true,
      canWrite: true,
      canDelete: true,
    };

    return acc;

  },
  {}
);


/* =========================================
   EDIT DETAILS
========================================= */

const EditDetails = ({
  data,
  refreshList,
}) => {

  const dispatch =
    useAppDispatch();


  const [role, setRole] =
    useState("");


  const [roleLoading, setRoleLoading] =
    useState(false);


  const roleId =
    data?._id;


  const isSuperadmin =
    data?.role?.toLowerCase() ===
    "superadmin";


  const permissionsForRole =
    useAppSelector(
      selectPermissionsForRole(roleId)
    );


  const loadingPerms =
    useAppSelector(
      selectPermissionsLoading(roleId)
    );


  const saving =
    useAppSelector(
      selectPermissionsSaving
    );


  /* =========================================
     SYNC ROLE NAME
  ========================================= */

  useEffect(() => {

    if (!data) return;

    setRole(
      data.role || ""
    );

  }, [data]);


  /* =========================================
     FETCH ROLE PERMISSIONS
  ========================================= */

  useEffect(() => {

    if (
      !roleId ||
      isSuperadmin
    ) {
      return;
    }


    dispatch(
      fetchPermissionsByRole(roleId)
    );

  }, [
    roleId,
    isSuperadmin,
    dispatch,
  ]);


  /* =========================================
     TOGGLE PERMISSION
  ========================================= */

  const handleToggle = (
    module,
    permKey
  ) => {

    if (!roleId) return;


    dispatch(
      togglePermission({

        roleId,

        module,

        permKey,

      })
    );

  };


  /* =========================================
     BUILD COMPLETE PERMISSION OBJECT

     IMPORTANT:
     This ensures that newly added modules
     like "assigned-bugs" are also saved.
  ========================================= */

  const getCompletePermissions = () => {

    const defaultPermissions =
      buildDefaultPermissions();


    const mergedPermissions =
      ALL_MODULES.reduce(
        (acc, { key }) => {

          acc[key] = {

            canRead:
              permissionsForRole?.[key]
                ?.canRead ?? false,

            canWrite:
              permissionsForRole?.[key]
                ?.canWrite ?? false,

            canDelete:
              permissionsForRole?.[key]
                ?.canDelete ?? false,

          };

          return acc;

        },
        defaultPermissions
      );


    return mergedPermissions;

  };


  /* =========================================
     UPDATE ROLE
  ========================================= */

  const handleSubmit =
    async (e) => {

      e.preventDefault();


      if (
        !role.trim()
      ) {

        showError(
          "Role name is required"
        );

        return;

      }


      if (
        !roleId
      ) {

        showError(
          "Role ID is missing"
        );

        return;

      }


      try {

        setRoleLoading(
          true
        );


        /* =====================================
           STEP 1
           UPDATE ROLE NAME
        ===================================== */

        const roleRes =
          await RoleApi.update({

            id: roleId,

            role:
              role.trim(),

          });


        if (
          roleRes?.code !== 200
        ) {

          showError(
            roleRes?.message ||
            "Failed to update role"
          );

          return;

        }


        /* =====================================
           STEP 2
           GET COMPLETE PERMISSIONS
        ===================================== */

        const completePermissions =
          getCompletePermissions();


        /* =====================================
           STEP 3
           SAVE PERMISSIONS
        ===================================== */

        const result =
          await dispatch(

            savePermissions({

              roleId,

              permissions:
                completePermissions,

            })

          );


        if (
          savePermissions.fulfilled.match(
            result
          )
        ) {

          showSuccess(
            "Role and permissions updated successfully"
          );


          refreshList?.();


          const offcanvas =
            document.getElementById(
              "offcanvas_edit"
            );


          if (
            offcanvas &&
            window.bootstrap
          ) {

            const instance =
              window.bootstrap.Offcanvas.getInstance(
                offcanvas
              );


            if (instance) {

              instance.hide();

            }

          }


        } else {

          showError(
            result.payload ||
            "Failed to save permissions"
          );

        }


      } catch (err) {

        console.error(
          "ROLE UPDATE ERROR:",
          err
        );


        showError(
          err?.message ||
          "Something went wrong"
        );


      } finally {

        setRoleLoading(
          false
        );

      }

    };


  /* =========================================
     NO DATA
  ========================================= */

  if (!data) {

    return null;

  }


  /* =========================================
     DISPLAY PERMISSIONS
  ========================================= */

  const displayPerms =
    isSuperadmin
      ? SUPERADMIN_PERMS
      : getCompletePermissions();


  /* =========================================
     UI
  ========================================= */

  return (

    <>

      {/* HEADER */}

      <div className="offcanvas-header border-bottom">

        <h5 className="fw-semibold">
          Edit Role
        </h5>


        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        />

      </div>


      <div className="offcanvas-body">


        <form
          onSubmit={handleSubmit}
        >


          {/* =================================
              ROLE NAME
          ================================= */}

          <div className="mb-3">

            <label className="form-label">

              Role Name

            </label>


            <input
              type="text"
              className="form-control"

              value={role}

              onChange={(e) =>
                setRole(
                  e.target.value
                )
              }

              disabled={
                isSuperadmin
              }
            />


            {isSuperadmin && (

              <small className="text-muted">

                Superadmin role cannot be modified.

              </small>

            )}

          </div>



          {/* =================================
              PERMISSIONS
          ================================= */}

          <div className="mb-3">

            <label className="form-label fw-semibold">

              Permissions

            </label>


            {loadingPerms ? (

              <div className="text-center py-4 text-muted">

                <span className="spinner-border spinner-border-sm me-2" />

                Loading permissions...

              </div>


            ) : (

              <div className="table-responsive">

                <table className="table table-bordered table-sm align-middle">


                  <thead className="table-light">

                    <tr>

                      <th>
                        Module
                      </th>

                      <th className="text-center">
                        Read
                      </th>

                      <th className="text-center">
                        Write
                      </th>

                      <th className="text-center">
                        Delete
                      </th>

                    </tr>

                  </thead>



                  <tbody>

                    {ALL_MODULES.map(
                      ({
                        key,
                        label,
                      }) => {

                        const perms =
                          displayPerms[key] || {

                            canRead: false,

                            canWrite: false,

                            canDelete: false,

                          };


                        return (

                          <tr
                            key={key}
                          >

                            <td>

                              {label}

                            </td>


                            {[
                              "canRead",

                              "canWrite",

                              "canDelete",

                            ].map(
                              (
                                permKey
                              ) => (

                                <td
                                  key={permKey}
                                  className="text-center"
                                >

                                  <input

                                    type="checkbox"

                                    className="form-check-input"

                                    checked={
                                      !!perms[
                                        permKey
                                      ]
                                    }

                                    disabled={
                                      isSuperadmin
                                    }

                                    onChange={() =>
                                      handleToggle(

                                        key,

                                        permKey

                                      )
                                    }

                                  />

                                </td>

                              )
                            )}

                          </tr>

                        );

                      }
                    )}

                  </tbody>


                </table>

              </div>

            )}

          </div>



          {/* =================================
              UPDATE BUTTON
          ================================= */}

          <button

            type="submit"

            className="btn btn-primary"

            disabled={
              saving ||
              roleLoading ||
              isSuperadmin ||
              loadingPerms
            }

          >

            {
              saving ||
              roleLoading
                ? (

                  <>

                    <span className="spinner-border spinner-border-sm me-1" />

                    Updating...

                  </>

                )
                : (

                  "Update"

                )
            }

          </button>


        </form>

      </div>

    </>

  );

};


export default EditDetails;